/**
 * PDP lightbox: click to zoom (QA R237 / R238 — "tıklayınca büyüteç açıyor ama büyümüyor").
 *
 * xo-gallery opens its lightbox (xo-gallery-portal, type "carousel") with one
 * <xo-image-zoom xo-zoom="1" xo-zoom-full> per slide. With xo-zoom="1" a click does nothing and only
 * the mouse wheel enlarges the image. This script toggles the zoom on click (desktop mouse only):
 *   - xo-zoom is set to CLICK_ZOOM (or back to 1) and xo-observed is bumped, which makes the
 *     framework re-read its options (ImageZoom.attributeChangedCallback → zoomState = xo-zoom);
 *   - a mousemove carrying the click position is dispatched on the element, so the framework's own
 *     handler applies the zoom at the clicked point (wheel zoom and mouse panning keep working).
 * Drags (moved more than DRAG_THRESHOLD px), touch/pen input and the page-level hover zoom are left
 * alone. Closing the lightbox or changing slides resets the zoom so the next open starts at 1x.
 * Loaded only on product pages (blocks/_product-info-media.liquid).
 */
(() => {
  if (window.__hpPdpLightboxZoom) return;
  window.__hpPdpLightboxZoom = true;

  const CLICK_ZOOM = 2.5;
  const DRAG_THRESHOLD = 6;
  const PORTAL_SELECTOR = 'xo-gallery-portal';
  const ZOOM_SELECTOR = 'xo-gallery-portal xo-image-zoom[xo-zoom-full]';
  const ZOOMED_ATTR = 'data-hp-zoomed';

  const mousePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
  const watchedPortals = new WeakSet();
  let observedSeq = 0;
  let pointerStart = null;

  function isOn(el, name) {
    const value = el.getAttribute(name);
    return value !== null && value !== 'false';
  }

  // The framework marks the element xo-active while its zoom level is above 1 (click or wheel).
  function isZoomed(el) {
    return isOn(el, 'xo-active');
  }

  function dispatchMove(el, clientX, clientY) {
    const rect = el.getBoundingClientRect();
    if (!rect.width || !rect.height) return;

    const event = new MouseEvent('mousemove', { bubbles: false, cancelable: true, clientX, clientY, view: window });
    // The framework handler reads offsetX/offsetY; pin them to this element so the value does not
    // depend on how a browser derives offsets for synthetic events.
    Object.defineProperty(event, 'offsetX', { value: ((clientX - rect.left) * el.offsetWidth) / rect.width });
    Object.defineProperty(event, 'offsetY', { value: ((clientY - rect.top) * el.offsetHeight) / rect.height });
    el.dispatchEvent(event);
  }

  function setZoom(el, level, clientX, clientY) {
    el.setAttribute('xo-zoom', String(level));
    el.toggleAttribute(ZOOMED_ATTR, level > 1);
    observedSeq += 1;
    el.setAttribute('xo-observed', `hp-${Date.now()}-${observedSeq}`);
    dispatchMove(el, clientX, clientY);
  }

  function resetPortal(portal) {
    portal.querySelectorAll('xo-image-zoom[xo-zoom-full]').forEach((el) => {
      if (!isZoomed(el) && !el.hasAttribute(ZOOMED_ATTR) && el.getAttribute('xo-zoom') === '1') return;
      const rect = el.getBoundingClientRect();
      setZoom(el, 1, rect.left + rect.width / 2, rect.top + rect.height / 2);
    });
  }

  function watchPortal(portal) {
    if (watchedPortals.has(portal)) return;
    watchedPortals.add(portal);

    new MutationObserver(() => {
      if (!isOn(portal, 'xo-active')) resetPortal(portal);
    }).observe(portal, { attributes: true, attributeFilter: ['xo-active'] });

    let scrollTimer = 0;
    portal.addEventListener(
      'scroll',
      () => {
        window.clearTimeout(scrollTimer);
        scrollTimer = window.setTimeout(() => resetPortal(portal), 120);
      },
      { passive: true },
    );
  }

  document.addEventListener(
    'pointerdown',
    (event) => {
      pointerStart = null;
      if (event.button !== 0 || event.pointerType !== 'mouse' || !mousePointer.matches) return;

      const portal = event.target instanceof Element ? event.target.closest(PORTAL_SELECTOR) : null;
      if (!portal) return;
      watchPortal(portal);

      const el = event.target.closest(ZOOM_SELECTOR);
      if (el) pointerStart = { el, x: event.clientX, y: event.clientY };
    },
    { capture: true, passive: true },
  );

  document.addEventListener(
    'click',
    (event) => {
      if (!(event.target instanceof Element)) return;
      const portal = event.target.closest(PORTAL_SELECTOR);
      if (!portal) return;

      const el = event.target.closest(ZOOM_SELECTOR);
      const start = pointerStart;
      pointerStart = null;

      // Arrows, counter or close button: drop the zoom of every slide in this lightbox.
      if (!el) {
        resetPortal(portal);
        return;
      }

      if (!start || start.el !== el || !mousePointer.matches) return;
      if (Math.abs(event.clientX - start.x) > DRAG_THRESHOLD || Math.abs(event.clientY - start.y) > DRAG_THRESHOLD) {
        return;
      }

      setZoom(el, isZoomed(el) ? 1 : CLICK_ZOOM, event.clientX, event.clientY);
    },
    true,
  );
})();
